import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'

import {
  fetchPreferences,
  sendPreferences,
  serialSender
} from '../lib/preferences-api.ts'
import {
  adoptServerPreferences,
  connectPreferenceSender,
  releaseForeignCache,
  takeDirtyChanges
} from '../lib/preference-store.ts'
import { QUIET_QUERY_META } from '../lib/query-activity.ts'

/**
 * PreferenceSync — joins the settings store to the employee's document.
 *
 * Renders nothing. Mounted once in the signed-in tree, it reads the document
 * once per sign-in, hands it to the store, and sends back what the server
 * lacks. From then on every change the reader makes is sent as it is made.
 *
 * **Another employee's cache goes on mount**, before the reader can press
 * anything: a change starts from what the store holds, so it would otherwise
 * carry that employee's lists into this one's document.
 *
 * **The sender joins only once the read has settled.** A change sent before
 * the answer arrives could be overwritten by that older answer; held until
 * then, it is uploaded instead. A read that fails still joins the sender, and
 * sends the changes held so far, so they are not lost with the answer.
 *
 * **Every send goes through one queue**, the upload and each later change
 * alike, because the backend's save is a read-merge-write with no
 * transaction — see `serialSender`. The queue belongs to one settled read and
 * closes with it, so nothing queued outlives the sign-in that queued it.
 *
 * Read once per sign-in, and never from a cache left by an earlier one: a save
 * made since then would be undone by adopting the older answer. A refetch on
 * focus would adopt the server's copy under a reader who is mid-change in
 * another tab, and the only gain is seeing another tab's choice without a
 * reload.
 *
 * @param props - Sync props.
 * @param props.viewer - The signed-in address. Keys the read, so a second
 *   employee signing in on this tab never adopts the first one's settings.
 * @returns Nothing.
 */
export function PreferenceSync({ viewer }: { viewer: string }): null {
  const query = useQuery({
    queryKey: ['preferences', viewer],
    meta: QUIET_QUERY_META,
    queryFn: fetchPreferences,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
    refetchOnWindowFocus: false,
    retry: 1
  })
  const settled = query.isSuccess || query.isError

  useEffect(() => {
    releaseForeignCache(viewer)
  }, [viewer])

  useEffect(() => {
    if (!settled) {
      return undefined
    }
    const queue = serialSender(sendPreferences)
    // Adopted before the sender joins, so no change is sent past the answer.
    const upload =
      query.data === undefined
        ? takeDirtyChanges()
        : adoptServerPreferences(query.data, viewer)
    // One request per section, so a cached value the backend refuses costs
    // that section alone rather than the whole upload.
    for (const [section, value] of Object.entries(upload)) {
      void queue.send({ [section]: value })
    }
    const disconnect = connectPreferenceSender(queue.send)

    return () => {
      queue.close()
      disconnect()
    }
  }, [settled, query.data, viewer])

  return null
}
